import { RRule, Weekday } from "rrule";
import { UtilsService } from "./utils.service";
import { format } from "date-fns";
import {
  RepeatEndOption,
  RepeatRuleOption,
  RepeatUnit,
  RepeatWeekday,
  TRRule,
} from "uis/modal/repeat-rule-setting-modal/types";

export class RRuleService {
  constructor(private readonly utilsService: UtilsService) {}

  //* INFO: RRule 문자열을 받아 해석된 문자열을 반환하는 함수
  transRRule(rruleStr: string) {
    try {
      const rrule = RRule.fromString(rruleStr);
      const options = rrule.options;

      const freqMap = {
        [RRule.DAILY]: "일",
        [RRule.WEEKLY]: "주",
        [RRule.MONTHLY]: "월",
        [RRule.YEARLY]: "년",
        [RRule.HOURLY]: "시간",
        [RRule.MINUTELY]: "분",
        [RRule.SECONDLY]: "초",
      };

      const dayMap = {
        0: "월",
        1: "화",
        2: "수",
        3: "목",
        4: "금",
        5: "토",
        6: "일",
        MO: "월",
        TU: "화",
        WE: "수",
        TH: "목",
        FR: "금",
        ST: "토",
        SU: "일",
      };

      // 빈 문자열로 초기화
      let result = "";

      // 반복 주기(FREQ) 해석
      if (options.freq in freqMap) {
        const interval = options.interval || 1;
        const unit = freqMap[options.freq];

        if (interval === 1) {
          result += `매${unit}`;
        } else {
          if (unit === "월") {
            result += `${interval}개월마다`;
          } else {
            result += `${interval}${unit}마다`;
          }
        }
      }

      // 특정 요일(BYDAY) 해석
      if (options.byweekday && options.byweekday.length > 0) {
        const days = [];
        options.byweekday.sort();
        if (JSON.stringify(options.byweekday) === JSON.stringify([0, 1, 2, 3, 4])) {
          result += ` 주중 월-금`;
        } else {
          for (const day of options.byweekday) {
            const dayName = dayMap[day.toString() as keyof typeof dayMap];
            days.push(dayName);
          }
          if (days.length == 1) {
            result += ` ${days.join(", ")}요일`;
          } else {
            result += ` ${days.join(", ")}`;
          }
        }
      }

      // bynweekday 해석 (BYNWEEKDAY)
      if (options.bynweekday && options.bynweekday.length > 0) {
        const days = [];
        for (const [weekday, nth] of options.bynweekday) {
          const nthText = this.getNthText(nth);
          const dayName = dayMap[weekday.toString() as keyof typeof dayMap];
          days.push(`${nthText} ${dayName}`);
        }
        if (days.length == 1) {
          result += ` ${days.join(", ")}요일`;
        } else {
          result += ` ${days.join(", ")}`;
        }
      }

      // 특정 일자(BYMONTHDAY) 해석
      if (options.bymonthday && options.bymonthday.length > 0) {
        result += ` ${options.bymonthday.join(", ")}일에`;
      }

      // 특정 월(BYMONTH) 해석
      if (options.bymonth && options.bymonth.length > 0) {
        const months = options.bymonth.map((month: any) => `${month}월`);
        result += ` ${months.join(", ")}에`;
      }

      // 종료 조건 (COUNT) 해석
      // if (options.count) {
      //     result += ` 총 ${options.count}번 반복`;
      // }

      // 종료 조건 (UNTIL) 해석
      // if (options.until) {
      //     const untilDate = options.until.toISOString().split('T')[0];
      //     result += ` 종료일: ${untilDate}`;
      // }

      // 시작 날짜 해석
      // if (options.dtstart) {
      //     const startDate = options.dtstart.toISOString().split('T')[0];
      //     result += ` 시작일: ${startDate}`;
      // }

      return result.trim();
    } catch (error) {
      console.error("RRule 파싱 에러:", error);
      return "유효하지 않은 RRule 문자열입니다.";
    }
  }

  //* INFO: RRule 문자열을 받아 시작일과 종료일을 반환하는 함수
  getPeriodOfRecurrence(rruleStr: string, startDate?: Date) {
    const rrule = RRule.fromString(rruleStr);
    const options = rrule.options;
    let terminatedAt = null;
    const updateRrule = RRule.fromString(`${rruleStr};DTSTART=${this.formatToRFC5545(startDate ?? new Date())}`);
    if (options.until) {
      const rrule = RRule.fromString(rruleStr);
      const options = rrule.options;
      terminatedAt = options.until
        ? new Date(options.until?.getUTCFullYear(), options.until?.getUTCMonth(), options.until?.getUTCDate())
        : null;
    } else if (options.count) {
      const occurrences = updateRrule.all();
      if (occurrences.length === 0) {
        throw new Error("반복 일정이 존재하지 않습니다.");
      }
      const lastOccurrence = occurrences[occurrences.length - 1];
      terminatedAt = lastOccurrence;
    } else {
      terminatedAt = null;
    }
    const startAt = updateRrule.after(startDate ?? new Date(), true);
    return { startAt, terminatedAt };
  }

  async updateRRuleStrToEndToday(rruleStr: string) {
    try {
      // const today = new Date();
      const formattedToday = await this.formatToRFC5545(await this.utilsService.getTodayMidnight());

      // 3️⃣ COUNT 또는 UNTIL 옵션 제거
      let newRRuleStr = rruleStr
        .replace(/COUNT=\d+;?/g, "") // COUNT= 제거
        .replace(/UNTIL=\d+T\d+Z;?/g, ""); // UNTIL= 제거

      // 4️⃣ 새로운 UNTIL 추가
      newRRuleStr = newRRuleStr.replace(/;$/, ""); // 마지막 ; 제거
      newRRuleStr = `${newRRuleStr};UNTIL=${formattedToday}`;

      return newRRuleStr;
    } catch (error) {
      console.error("RRule 종료 옵션 변경 에러:", error);
      return "유효하지 않은 RRule 문자열입니다.";
    }
  }

  getNthText(nth: number) {
    if (nth === 1) return "1번째";
    if (nth === 2) return "2번째";
    if (nth === 3) return "3번째";
    if (nth === 4) return "4번째";
    if (nth === 5) return "5번째";
    if (nth === -1) return "마지막";
    if (nth === -2) return "마지막에서 두 번째";
    return `${nth}번째`;
  }

  formatToRFC5545(date: Date): string {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");
    return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
  }

  /**
   * Helper function to calculate Nth weekday (e.g., 3WE or 4TH).
   */
  calculateNthWeekday(date: Date): string | null {
    const weekdayMap = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
    const day = date.getDay(); // 요일 (0=Sunday, ..., 6=Saturday)
    const dayOfMonth = date.getDate(); // 일자
    const nthWeek = Math.ceil(dayOfMonth / 7); // 몇 번째 주인지 계산
    const weekday = weekdayMap[day]; // 요일을 RRULE 형식으로 변환

    return nthWeek && weekday ? `${nthWeek}${weekday}` : null;
  }

  //* INFO: 반복 규칙 객체 RRule를 문자열로 변환하는 함수
  generateRRuleString(rrule: TRRule, startDate?: Date): string {
    const parts: string[] = [];

    // Repeat frequency (FREQ)
    switch (rrule.repeatUnit) {
      case RepeatUnit.DAY:
        parts.push("RRULE:FREQ=DAILY");
        break;
      case RepeatUnit.WEEK:
        parts.push("RRULE:FREQ=WEEKLY");
        break;
      case RepeatUnit.MONTH:
        parts.push("RRULE:FREQ=MONTHLY");
        break;
      case RepeatUnit.WEEKDAYS:
        parts.push("RRULE:FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR");
        break;
      default:
        throw new Error("Invalid repeat unit");
    }

    if (rrule.repeatUnit === RepeatUnit.WEEK && rrule.repeatWeekdays) {
      //* INFO: rrule.repeatWeekdays가 존재하면, BYDAY 옵션을 추가
      if (rrule.repeatWeekdays.length > 0) {
        const weekdays = rrule.repeatWeekdays.map((weekday) => {
          switch (weekday) {
            case RepeatWeekday.MON:
              return "MO";
            case RepeatWeekday.TUE:
              return "TU";
            case RepeatWeekday.WED:
              return "WE";
            case RepeatWeekday.THU:
              return "TH";
            case RepeatWeekday.FRI:
              return "FR";
            case RepeatWeekday.SAT:
              return "SA";
            case RepeatWeekday.SUN:
              return "SU";
            default:
              throw new Error("Invalid repeat weekday");
          }
        });
        parts.push(`BYDAY=${weekdays.join(",")}`);
      }
    }

    // Interval (INTERVAL)
    if (rrule.interval > 1) {
      parts.push(`INTERVAL=${rrule.interval}`);
    }

    // End option (UNTIL or COUNT)
    if (rrule.repeatEndOption === RepeatEndOption.DATE && rrule.endDate) {
      const updateUntilDate = new Date(rrule.endDate);
      updateUntilDate.setHours(23, 59, 0, 0);

      const untilDate = format(new Date(updateUntilDate), "yyyyMMdd'T'HHmmss'Z'");
      parts.push(`UNTIL=${untilDate}`);
    } else if (rrule.repeatEndOption === RepeatEndOption.TIMES && rrule.repeatCount) {
      parts.push(`COUNT=${rrule.repeatCount}`);
    }

    // Repeat rule option (BYSETPOS or BYDAY)
    if (rrule.repeatUnit === RepeatUnit.MONTH) {
      if (rrule.repeatRuleOption === RepeatRuleOption.NTH_WEEKDAY) {
        const nthWeekday = this.calculateNthWeekday(startDate ?? new Date());
        if (nthWeekday) {
          parts.push(`BYDAY=${nthWeekday}`);
        }
      }
      if (rrule.repeatRuleOption === RepeatRuleOption.SPECIFIC) parts.push(`BYMONTHDAY=${startDate?.getDate()}`);
    }

    return parts.join(";");
  }

  //* INFO: 반복 규칙과 반복 예약 시작일을 받아, 이미 진행된 일정의 횟수를 반환하는 함수
  countPastOccurrences(rruleStr: string, startDate: Date): number {
    const rrule = RRule.fromString(rruleStr);

    const occurrences = rrule.between(new Date(), startDate);
    return occurrences.length;
  }

  //* INFO: RRule 문자열을 해석해 반복 규칙 객체 TRRule를 생성하는 함수
  parseRRuleString(rruleStr: string): TRRule {
    const rrule = RRule.fromString(rruleStr);
    const options = rrule.options;

    const repeatUnitMap: Record<number, RepeatUnit> = {
      [RRule.DAILY]: RepeatUnit.DAY,
      [RRule.WEEKLY]: RepeatUnit.WEEK,
      [RRule.MONTHLY]: RepeatUnit.MONTH,
    };

    const repeatWeekdayMap: Record<number, RepeatWeekday> = {
      [RRule.MO.weekday]: RepeatWeekday.MON,
      [RRule.TU.weekday]: RepeatWeekday.TUE,
      [RRule.WE.weekday]: RepeatWeekday.WED,
      [RRule.TH.weekday]: RepeatWeekday.THU,
      [RRule.FR.weekday]: RepeatWeekday.FRI,
      [RRule.SA.weekday]: RepeatWeekday.SAT,
      [RRule.SU.weekday]: RepeatWeekday.SUN,
    };

    const repeatUnit = repeatUnitMap[options.freq] || RepeatUnit.WEEK;
    const interval = options.interval || 1;

    let repeatEndOption: RepeatEndOption = RepeatEndOption.DATE;
    let repeatCount: number | undefined;
    let endDate: Date | undefined;

    if (options.count) {
      repeatEndOption = RepeatEndOption.TIMES;
      repeatCount = options.count;
    } else if (options.until) {
      repeatEndOption = RepeatEndOption.DATE;

      // 날짜만 유지하고 시간을 초기화
      const originalUntil = options.until;

      endDate = new Date(originalUntil.getUTCFullYear(), originalUntil.getUTCMonth(), originalUntil.getUTCDate());
    }

    const repeatRuleOption: RepeatRuleOption =
      options.bynweekday && options.bynweekday.length > 0 ? RepeatRuleOption.NTH_WEEKDAY : RepeatRuleOption.SPECIFIC;

    // Map weekdays
    const repeatWeekdays =
      options.byweekday
        ?.map((day) => {
          // Check if day has a weekday property (Weekday object)
          if (typeof day === "object" && "weekday" in day) {
            return repeatWeekdayMap[(day as Weekday).weekday];
          } else if (typeof day === "number") {
            // If day is a number
            return repeatWeekdayMap[day];
          }
          return null; // Handle invalid case
        })
        .filter((day): day is RepeatWeekday => day !== null) || [];

    return {
      repeatUnit,
      interval,
      repeatEndOption,
      repeatCount,
      endDate,
      repeatRuleOption,
      repeatWeekdays,
    };
  }
}
